import { h } from 'preact'
import { useContext } from 'preact/hooks'
import { DriveStoreContext, DriveI18nContext, DriveUiContext } from './drive-context.js'
import { UploaderCell } from './UploaderCell.jsx'
import { RowActions } from './RowActions.jsx'
import { FileTagChips, FileTagPicker } from './FileTagPicker.jsx'
import { openDriveEditor } from '../../modules/drive/doc-editor.js'

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(iso) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleDateString('hu-HU', { year: 'numeric', month: '2-digit', day: '2-digit' })
  } catch {
    return iso
  }
}

function fileIcon(contentType) {
  if (!contentType) return 'bi-file-earmark'
  if (contentType.startsWith('image/'))       return 'bi-file-earmark-image text-info'
  if (contentType === 'application/pdf')       return 'bi-file-earmark-pdf text-danger'
  if (contentType.includes('spreadsheet') || contentType.includes('excel')) return 'bi-file-earmark-spreadsheet text-success'
  if (contentType.includes('word') || contentType.includes('document'))     return 'bi-file-earmark-word text-primary'
  if (contentType.includes('presentation') || contentType.includes('powerpoint')) return 'bi-file-earmark-ppt text-warning'
  if (contentType.startsWith('video/'))       return 'bi-file-earmark-play text-purple'
  if (contentType.startsWith('audio/'))       return 'bi-file-earmark-music'
  if (contentType.includes('zip') || contentType.includes('compressed')) return 'bi-file-earmark-zip'
  return 'bi-file-earmark'
}

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']

export function FileRow({ file, selectedIds = [] }) {
  const store      = useContext(DriveStoreContext)
  const i18n       = useContext(DriveI18nContext)
  const ui         = useContext(DriveUiContext)
  const t          = (key) => i18n ? i18n.t(key) : key
  const itemId     = `file-${file.id}`
  const isSelected = selectedIds.includes(itemId)
  const isImage    = IMAGE_TYPES.includes(file.content_type)
  // Prefer generated preview; for images fall back to original so thumb shows
  // immediately after upload while PreviewProcessorJob still runs.
  const thumbUrl   = file.preview_url || (isImage ? file.original_url : null)

  function handleRowClick(e) {
    if (e.target.closest('a, button, .drive-item-thumb, .dropdown-menu')) return
    store.selectItem(itemId, e.metaKey || e.ctrlKey)
  }

  function handleThumbSelect(e) {
    e.preventDefault()
    e.stopPropagation()
    // Same as Space: multi-toggle so checkbox add/remove without clearing others
    store.selectItem(itemId, true)
  }

  const fileLink = file.editor_supported && file.editor_config_url ? (
    <a
      href="#"
      class="text-decoration-none fw-medium"
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        openDriveEditor({
          blobId: file.blob_id,
          configUrl: file.editor_config_url,
          filename: file.filename
        })
      }}
    >
      {file.filename}
    </a>
  ) : isImage ? (
    <a
      href={file.original_url || '#'}
      class="text-decoration-none fw-medium"
      data-photoswipe="drive-gallery"
      data-pswp-src={file.original_url}
      data-type="image"
      data-caption={file.filename}
    >
      {file.filename}
    </a>
  ) : file.is_task_pdf ? (
    <a
      href={file.pdf_viewer_url || file.original_url || '#'}
      class="text-decoration-none fw-medium"
      data-photoswipe="drive-gallery"
      data-pswp-src={file.pdf_viewer_url || file.original_url}
      data-type="iframe"
      data-caption={file.filename}
    >
      {file.filename}
    </a>
  ) : (
    <a
      href={file.download_url || `/drive/files/${file.id}/download`}
      class="text-decoration-none fw-medium"
    >
      {file.filename}
    </a>
  )

  const actions = [
    {
      key: 'download',
      label: t('drive.download_file'),
      icon: 'bi-download',
      buttonClass: 'btn-outline-secondary',
      onClick: () => { window.location.href = file.download_url || `/drive/files/${file.id}/download` },
    },
    {
      key: 'share',
      label: t('drive.share_this'),
      icon: 'bi-share',
      buttonClass: 'btn-outline-info',
      onClick: () => ui?.openShare?.({ type: 'file', id: file.id }),
    },
    {
      key: 'permissions',
      label: t('drive.manage_permissions_title'),
      icon: 'bi-shield-lock',
      buttonClass: 'btn-outline-secondary',
      onClick: () => ui?.openPermissions?.({ type: 'file', id: file.id }),
    },
    {
      key: 'delete',
      label: t('drive.delete_item'),
      icon: 'bi-trash',
      buttonClass: 'btn-outline-danger',
      danger: true,
      onClick: () => window._driveHost?.deleteFileById?.(file.id),
    },
  ]

  const menuActions = ui?.canUpdate ? [
    {
      key: 'rename',
      label: t('drive.rename'),
      icon: 'bi-pencil',
      onClick: () => ui.openRename({ type: 'file', id: file.id, name: file.filename }),
    },
    {
      key: 'move',
      label: t('drive.move'),
      icon: 'bi-folder-symlink',
      onClick: () => ui.requestMove(itemId),
    },
  ] : []

  return (
    <tr
      class={isSelected ? 'table-active' : ''}
      aria-selected={isSelected}
      data-item-id={file.id}
      data-item-type="file"
      tabindex="0"
      onClick={handleRowClick}
      style="cursor: pointer;"
    >
      <td class="text-center align-middle">
        <div
          class={`drive-item-thumb${isSelected ? ' is-selected' : ''}`}
          role="presentation"
        >
          <span
            class="drive-item-thumb__preview"
            aria-hidden="true"
            onClick={handleThumbSelect}
          >
            {thumbUrl ? (
              <img src={thumbUrl} alt="" loading="lazy" decoding="async" />
            ) : (
              <i class={`bi ${fileIcon(file.content_type)}`}></i>
            )}
          </span>
          <label
            class="drive-item-thumb__check"
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="checkbox"
              checked={isSelected}
              aria-label={t('drive.select_item')}
              onClick={(e) => e.stopPropagation()}
              onChange={handleThumbSelect}
            />
          </label>
        </div>
      </td>
      <td class="align-middle">
        <div class="d-inline-flex flex-wrap align-items-center gap-1">
          {fileLink}
          <FileTagChips tags={file.tags} />
          <FileTagPicker file={file} />
        </div>
      </td>
      <UploaderCell person={file.uploaded_by} />
      <td class="d-none d-md-table-cell align-middle text-muted">
        <small>{formatBytes(file.byte_size)}</small>
      </td>
      <td class="d-none d-md-table-cell align-middle text-muted">
        <small>{formatDate(file.created_at)}</small>
      </td>
      <RowActions actions={actions} menuActions={menuActions} />
    </tr>
  )
}
